import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  useExpenseInvoiceQuery,
  useExpenseInvoiceSupplierPrepaymentPreviewQuery,
  useExpenseInvoiceSupplierPrepaymentSettlementsQuery,
  useReleaseSupplierPrepaymentAllocation,
  useSettleExpenseInvoiceFromPrepayment,
  useFinalizeExpenseInvoiceImportReviewMutation,
} from '../Service/extendedExpenseInvoicesApi';
import {
  Alert,
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
  Chip,
  Grid,
  Divider,
  TextField,
} from '@mui/material';
import ExpenseInvoiceStatusBadge from '../Components/ExpenseInvoiceStatusBadge';
import ExpenseInvoiceDocumentKindBadge from '../Components/ExpenseInvoiceDocumentKindBadge';
import ExpenseInvoiceFa3SemanticPanel from '../Components/ExpenseInvoiceFa3SemanticPanel';
import {
  ExpenseInvoiceDraftBlockReason,
  ExpenseInvoiceStatus,
  type ExpenseInvoiceItemDto,
} from '../Types/expenseInvoice.types';
import { ExpenseInvoicesRoutes } from '../../../Router/path';
import { useAuthUser } from '../../Auth/hooks/useAuthHooks';
import { extractApiErrorMessage } from '../../../Utils/apiErrorUtils';
import { isSupplierPrepaymentsEnabled } from '../../../Config/featureFlags';
import {
  isFullExpenseInvoiceDocument,
  isSimplifiedExpenseInvoiceDocument,
} from '../Utils/expenseInvoiceDocumentKind';
import {
  getExpenseInvoiceDraftBlockReasonLabel,
  normalizeExpenseInvoiceDraftBlockReason,
  shouldShowExpenseInvoiceDraftBlockReason,
} from '../Utils/expenseInvoiceDraftBlockReason';
import { formatExpenseInvoiceDate } from '../Utils/expenseInvoiceDate';

const formatCurrency = (value: number, currency: string = 'PLN') =>
  `${value.toFixed(2)} ${currency}`;

export const ExpenseInvoiceDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const authUser = useAuthUser();
  const invoiceId = Number(id);

  const [isSettleDialogOpen, setIsSettleDialogOpen] = useState(false);
  const [isReleaseDialogOpen, setIsReleaseDialogOpen] = useState(false);
  const [isFinalizeDialogOpen, setIsFinalizeDialogOpen] = useState(false);
  const [settlementAmountInput, setSettlementAmountInput] = useState('0.00');
  const [settlementDescription, setSettlementDescription] = useState('');
  const [releaseDescription, setReleaseDescription] = useState('');
  const [settlementError, setSettlementError] = useState<string | null>(null);
  const [releaseError, setReleaseError] = useState<string | null>(null);
  const [finalizeError, setFinalizeError] = useState<string | null>(null);

  const { data, isLoading, isError, refetch } = useExpenseInvoiceQuery(invoiceId, {
    skip: !Number.isFinite(invoiceId) || invoiceId <= 0,
  });
  const isSimplifiedDocument = isSimplifiedExpenseInvoiceDocument(data?.documentKind);
  const isFullInvoiceDocument = isFullExpenseInvoiceDocument(data?.documentKind);

  const {
    data: preview,
    isLoading: isPreviewLoading,
    isFetching: isPreviewFetching,
  } = useExpenseInvoiceSupplierPrepaymentPreviewQuery(invoiceId, {
    skip:
      !isSupplierPrepaymentsEnabled ||
      !Number.isFinite(invoiceId) ||
      !data ||
      !isFullInvoiceDocument,
  });
  const {
    data: settlements,
    isLoading: isSettlementsLoading,
    refetch: refetchSettlements,
  } = useExpenseInvoiceSupplierPrepaymentSettlementsQuery(invoiceId, {
    skip:
      !isSupplierPrepaymentsEnabled ||
      !Number.isFinite(invoiceId) ||
      !data ||
      !isFullInvoiceDocument,
  });
  const [settleFromPrepayment, { isLoading: isSettling }] =
    useSettleExpenseInvoiceFromPrepayment();
  const [releaseAllocation, { isLoading: isReleasing }] =
    useReleaseSupplierPrepaymentAllocation();
  const [finalizeImportReview, { isLoading: isFinalizing }] =
    useFinalizeExpenseInvoiceImportReviewMutation();

  const items: ExpenseInvoiceItemDto[] = useMemo(() => data?.items ?? [], [data]);
  const calculatedDueAmount = useMemo(() => {
    if (!data) {
      return 0;
    }

    const paidAmount = data.paidAmount ?? 0;
    return Math.max(data.totalGross - paidAmount, 0);
  }, [data]);

  const previewDueAmount = preview?.dueAmount ?? calculatedDueAmount;
  const previewOpenBalance = preview?.openPrepaymentBalance ?? 0;
  const maxSettleAmount = Math.max(Math.min(previewDueAmount, previewOpenBalance), 0);
  const latestSettlement = useMemo(() => {
    if (!settlements || settlements.length === 0) {
      return null;
    }

    return settlements[0];
  }, [settlements]);

  useEffect(() => {
    if (
      !isSettleDialogOpen ||
      isPreviewLoading ||
      isPreviewFetching ||
      !preview ||
      !isFullInvoiceDocument
    ) {
      return;
    }

    setSettlementAmountInput(maxSettleAmount.toFixed(2));
  }, [
    isSettleDialogOpen,
    isPreviewLoading,
    isPreviewFetching,
    preview,
    maxSettleAmount,
    isFullInvoiceDocument,
  ]);

  const handleOpenSettlementDialog = () => {
    setSettlementError(null);
    setSettlementDescription('');
    setSettlementAmountInput('0.00');
    setIsSettleDialogOpen(true);
  };

  const handleCloseSettlementDialog = () => {
    if (isSettling) {
      return;
    }

    setIsSettleDialogOpen(false);
    setSettlementError(null);
  };

  const handleSubmitSettlement = async () => {
    if (!data || !isFullInvoiceDocument) {
      return;
    }

    if (!authUser?.id) {
      setSettlementError('Brak danych użytkownika do wykonania rozliczenia.');
      return;
    }

    const normalizedInput = settlementAmountInput.replace(',', '.').trim();
    const parsedAmount = Number(normalizedInput);

    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
      setSettlementError('Kwota rozliczenia musi być większa od zera.');
      return;
    }

    const roundedAmount = Number(parsedAmount.toFixed(2));
    if (roundedAmount > maxSettleAmount) {
      setSettlementError(
        `Kwota rozliczenia nie może przekroczyć ${formatCurrency(maxSettleAmount, data.currency)}.`
      );
      return;
    }

    try {
      setSettlementError(null);

      await settleFromPrepayment({
        invoiceId: data.id,
        amount: roundedAmount,
        createdByUserId: authUser.id,
        description: settlementDescription.trim() || undefined,
      }).unwrap();

      setIsSettleDialogOpen(false);
      setSettlementDescription('');
      setSettlementAmountInput('0.00');

      await Promise.all([refetch(), refetchSettlements()]);
    } catch (error) {
      setSettlementError(
        extractApiErrorMessage(error, 'Nie udało się rozliczyć faktury z przedpłaty dostawcy.')
      );
    }
  };

  const handleOpenReleaseDialog = () => {
    setReleaseError(null);
    setReleaseDescription('');
    setIsReleaseDialogOpen(true);
  };

  const handleCloseReleaseDialog = () => {
    if (isReleasing) {
      return;
    }

    setIsReleaseDialogOpen(false);
    setReleaseError(null);
  };

  const handleSubmitRelease = async () => {
    if (!latestSettlement || !isFullInvoiceDocument) {
      setReleaseError('Brak aktywnego rozliczenia do cofnięcia.');
      return;
    }

    if (!authUser?.id) {
      setReleaseError('Brak danych użytkownika do wykonania cofnięcia rozliczenia.');
      return;
    }

    try {
      setReleaseError(null);

      await releaseAllocation({
        settlementId: latestSettlement.settlementId,
        createdByUserId: authUser.id,
        description: releaseDescription.trim() || undefined,
      }).unwrap();

      setIsReleaseDialogOpen(false);
      setReleaseDescription('');

      await Promise.all([refetch(), refetchSettlements()]);
    } catch (error) {
      setReleaseError(
        extractApiErrorMessage(error, 'Nie udało się cofnąć rozliczenia z przedpłaty.')
      );
    }
  };

  const handleOpenFinalizeDialog = () => {
    setFinalizeError(null);
    setIsFinalizeDialogOpen(true);
  };

  const handleCloseFinalizeDialog = () => {
    if (isFinalizing) {
      return;
    }

    setIsFinalizeDialogOpen(false);
    setFinalizeError(null);
  };

  const handleConfirmFinalize = async () => {
    if (
      !data ||
      !isFullInvoiceDocument ||
      data.status !== ExpenseInvoiceStatus.Draft ||
      normalizeExpenseInvoiceDraftBlockReason(data.draftBlockReason) !==
        ExpenseInvoiceDraftBlockReason.ImportDataReviewRequired
    ) {
      return;
    }

    try {
      setFinalizeError(null);

      await finalizeImportReview(data.id).unwrap();

      setIsFinalizeDialogOpen(false);
      await refetch();
    } catch (error) {
      setFinalizeError(
        extractApiErrorMessage(error, 'Nie udało się sfinalizować weryfikacji importu.')
      );
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError || !data) {
    return (
      <Box p={4}>
        <Typography color="error">Nie udało się pobrać szczegółów dokumentu kosztowego.</Typography>
      </Box>
    );
  }

  const shouldShowDraftReason = shouldShowExpenseInvoiceDraftBlockReason(
    data.status,
    data.draftBlockReason
  );
  // Only supported way to move an OCR-imported draft to Received (R9). Never derived for
  // SimplifiedCost documents or any status/block-reason combination other than exactly
  // Draft + ImportDataReviewRequired.
  const canFinalizeImportReview =
    isFullInvoiceDocument &&
    data.status === ExpenseInvoiceStatus.Draft &&
    normalizeExpenseInvoiceDraftBlockReason(data.draftBlockReason) ===
      ExpenseInvoiceDraftBlockReason.ImportDataReviewRequired;

  return (
    <Box p={3}>
      <Stack direction="row" alignItems="center" spacing={2} mb={3} flexWrap="wrap" useFlexGap>
        <Typography variant="h4">Dokument kosztowy {data.documentReference}</Typography>
        <ExpenseInvoiceDocumentKindBadge documentKind={data.documentKind} size="medium" />
        <ExpenseInvoiceStatusBadge status={data.status} />
        {!isSimplifiedDocument && data.isPaid && (
          <Chip label="Opłacona" color="success" size="small" />
        )}
        <Box flexGrow={1} />
        <Button variant="outlined" onClick={() => navigate(-1)}>
          Powrót
        </Button>
        {isSupplierPrepaymentsEnabled && isFullInvoiceDocument && !data.isPaid && (
          <Stack spacing={0.5} alignItems="flex-end">
            <Stack direction="row" spacing={1}>
              <Button variant="contained" color="primary" onClick={handleOpenSettlementDialog}>
                Rozlicz z przedpłaty
              </Button>
              {latestSettlement && (
                <Button
                  variant="outlined"
                  color="warning"
                  onClick={handleOpenReleaseDialog}
                  disabled={isSettlementsLoading}
                >
                  Cofnij rozliczenie
                </Button>
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Rozliczenie wykorzystuje saldo otwartej przedpłaty dostawcy.
            </Typography>
          </Stack>
        )}
      </Stack>

      {isSimplifiedDocument && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Koszt uproszczony nie uczestniczy w payment tracking ani w rozliczeniach z przedpłaty
          dostawcy.
        </Alert>
      )}

      {shouldShowDraftReason && (
        <Alert
          severity="warning"
          sx={{ mb: 3 }}
          action={
            canFinalizeImportReview ? (
              <Button color="inherit" size="small" onClick={handleOpenFinalizeDialog}>
                Sfinalizuj weryfikację
              </Button>
            ) : undefined
          }
        >
          Przyczyna pozostawienia dokumentu w szkicu:{' '}
          <strong>{getExpenseInvoiceDraftBlockReasonLabel(data.draftBlockReason)}</strong>.
        </Alert>
      )}

      <Dialog open={canFinalizeImportReview && isFinalizeDialogOpen} onClose={handleCloseFinalizeDialog} fullWidth maxWidth="sm">
        <DialogTitle>Finalizacja weryfikacji importu</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {finalizeError && <Alert severity="error">{finalizeError}</Alert>}
            <Alert severity="info">
              Dokument przejdzie ze statusu <strong>Szkic</strong> do statusu{' '}
              <strong>Odebrana</strong>. Ta operacja jest jedynym sposobem zatwierdzenia
              zaimportowanego dokumentu wymagającego ręcznej weryfikacji danych.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFinalizeDialog} disabled={isFinalizing}>
            Anuluj
          </Button>
          <Button variant="contained" onClick={handleConfirmFinalize} disabled={isFinalizing}>
            {isFinalizing ? 'Finalizowanie...' : 'Potwierdź finalizację'}
          </Button>
        </DialogActions>
      </Dialog>

      {data.fa3Semantic ? (
        <ExpenseInvoiceFa3SemanticPanel semantic={data.fa3Semantic} items={items} />
      ) : null}

      {isSupplierPrepaymentsEnabled && isFullInvoiceDocument && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Podsumowanie rozliczeń
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Zapłacono
                </Typography>
                <Typography variant="h6">
                  {formatCurrency(preview?.paidAmount ?? data.paidAmount ?? 0, data.currency)}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Pozostało
                </Typography>
                <Typography variant="h6">
                  {formatCurrency(previewDueAmount, data.currency)}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Otwarte saldo przedpłaty
                </Typography>
                <Typography variant="h6">
                  {isPreviewLoading || isPreviewFetching
                    ? 'Ładowanie...'
                    : formatCurrency(previewOpenBalance, data.currency)}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      <Dialog
        open={isSupplierPrepaymentsEnabled && isFullInvoiceDocument && isSettleDialogOpen}
        onClose={handleCloseSettlementDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Rozlicz fakturę z przedpłaty dostawcy</DialogTitle>
        <DialogContent>
          {(isPreviewLoading || isPreviewFetching) && (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress size={24} />
            </Box>
          )}

          {!isPreviewLoading && !isPreviewFetching && (
            <Stack spacing={2} mt={1}>
              {settlementError && <Alert severity="error">{settlementError}</Alert>}

              <Card variant="outlined">
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <Typography variant="caption" color="text.secondary">
                        Zapłacono
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {formatCurrency(preview?.paidAmount ?? data.paidAmount ?? 0, data.currency)}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <Typography variant="caption" color="text.secondary">
                        Pozostało
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {formatCurrency(previewDueAmount, data.currency)}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <Typography variant="caption" color="text.secondary">
                        Otwarte saldo przedpłaty
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {formatCurrency(previewOpenBalance, data.currency)}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              <TextField
                label="Kwota rozliczenia"
                type="number"
                value={settlementAmountInput}
                onChange={(event) => {
                  setSettlementAmountInput(event.target.value);
                  if (settlementError) {
                    setSettlementError(null);
                  }
                }}
                inputProps={{ min: 0, step: 0.01, max: maxSettleAmount }}
                fullWidth
                disabled={maxSettleAmount <= 0 || isSettling}
              />

              <TextField
                label="Opis operacji (opcjonalnie)"
                value={settlementDescription}
                onChange={(event) => setSettlementDescription(event.target.value)}
                fullWidth
                multiline
                minRows={2}
                disabled={isSettling}
              />

              {maxSettleAmount <= 0 && (
                <Alert severity="warning">
                  Brak dostępnej kwoty do rozliczenia (saldo przedpłaty lub kwota pozostała wynosi
                  0).
                </Alert>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSettlementDialog} disabled={isSettling}>
            Anuluj
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitSettlement}
            disabled={isSettling || isPreviewLoading || isPreviewFetching || maxSettleAmount <= 0}
          >
            {isSettling ? 'Trwa rozliczanie...' : 'Rozlicz'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isSupplierPrepaymentsEnabled && isFullInvoiceDocument && isReleaseDialogOpen}
        onClose={handleCloseReleaseDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Cofnij rozliczenie przedpłaty</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {releaseError && <Alert severity="error">{releaseError}</Alert>}

            <Alert severity="warning">
              Ta operacja cofnie ostatnie rozliczenie przedpłaty dla tej faktury. Po wykonaniu
              wzrośnie kwota <strong>Pozostało</strong> na fakturze, a saldo{' '}
              <strong>Otwarte saldo przedpłaty</strong> zostanie przywrócone.
            </Alert>

            {latestSettlement && (
              <Card variant="outlined">
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="caption" color="text.secondary">
                        Kwota rozliczenia do cofnięcia
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {formatCurrency(latestSettlement.settledAmount, data.currency)}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="caption" color="text.secondary">
                        Data rozliczenia
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {new Date(latestSettlement.createdAtUtc).toLocaleString('pl-PL')}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            )}

            <TextField
              label="Powód cofnięcia (opcjonalnie)"
              value={releaseDescription}
              onChange={(event) => setReleaseDescription(event.target.value)}
              fullWidth
              multiline
              minRows={2}
              disabled={isReleasing}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseReleaseDialog} disabled={isReleasing}>
            Anuluj
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleSubmitRelease}
            disabled={isReleasing || !latestSettlement}
          >
            {isReleasing ? 'Trwa cofanie...' : 'Potwierdź cofnięcie'}
          </Button>
        </DialogActions>
      </Dialog>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Informacje podstawowe
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Referencja dokumentu
              </Typography>
              <Typography variant="body1">{data.documentReference}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Data wystawienia
              </Typography>
              <Typography variant="body1">{formatExpenseInvoiceDate(data.issueDate)}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="subtitle2" color="text.secondary">
                {isSimplifiedDocument ? 'Firma' : 'Data sprzedaży'}
              </Typography>
              <Typography variant="body1">
                {isSimplifiedDocument ? data.companyName || '-' : formatExpenseInvoiceDate(data.saleDate)}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="subtitle2" color="text.secondary">
                {isSimplifiedDocument ? 'Waluta' : 'Termin płatności'}
              </Typography>
              <Typography variant="body1">
                {isSimplifiedDocument ? data.currency : formatExpenseInvoiceDate(data.paymentDueDate)}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {isSimplifiedDocument ? 'Klasyfikacja dokumentu' : 'Dane dostawcy'}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="subtitle2" color="text.secondary">
                {isSimplifiedDocument ? 'Rodzaj dokumentu' : 'Nazwa'}
              </Typography>
              <Typography variant="body1">{data.displayIssuerName}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="subtitle2" color="text.secondary">
                {isSimplifiedDocument ? 'Tagi' : 'NIP'}
              </Typography>
              {isSimplifiedDocument ? (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {data.tags.length > 0 ? (
                    data.tags.map((tag) => <Chip key={tag.id} label={tag.name} size="small" />)
                  ) : (
                    <Typography variant="body1">-</Typography>
                  )}
                </Stack>
              ) : (
                <Typography variant="body1">{data.supplierNip || '-'}</Typography>
              )}
            </Grid>
            {!isSimplifiedDocument && (
              <>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Adres
                  </Typography>
                  <Typography variant="body1">{data.supplierAddress || '-'}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Konto bankowe
                  </Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                    {data.supplierBankAccount || '-'}
                  </Typography>
                </Grid>
              </>
            )}
          </Grid>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Podsumowanie kwot
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Suma netto
              </Typography>
              <Typography variant="h6">{formatCurrency(data.totalNet, data.currency)}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Suma VAT
              </Typography>
              <Typography variant="h6">{formatCurrency(data.totalVat, data.currency)}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Suma brutto
              </Typography>
              <Typography variant="h6" color="primary">
                {formatCurrency(data.totalGross, data.currency)}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {isFullInvoiceDocument && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Pozycje faktury
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                    <TableCell>#</TableCell>
                    <TableCell>Nazwa</TableCell>
                    <TableCell align="right">Ilość</TableCell>
                    <TableCell align="right">Cena netto</TableCell>
                    <TableCell align="right">Stawka VAT</TableCell>
                    <TableCell align="right">Kwota netto</TableCell>
                    <TableCell align="right">Kwota VAT</TableCell>
                    <TableCell align="right">Kwota brutto</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                          Brak pozycji
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    items.map((item, idx) => (
                      <TableRow key={item.id || idx}>
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell>
                          {item.name}
                          {item.isVatExempt && (
                            <Typography variant="caption" display="block" color="text.secondary">
                              Zwolnione z VAT: {item.vatExemptNote || '-'}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">{item.quantity}</TableCell>
                        <TableCell align="right">
                          {formatCurrency(item.unitNet, data.currency)}
                        </TableCell>
                        <TableCell align="right">
                          {item.isVatExempt ? 'ZW' : `${(item.vatRate * 100).toFixed(0)}%`}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(item.totalNet, data.currency)}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(item.totalVat, data.currency)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                          {formatCurrency(item.totalGross, data.currency)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {isSimplifiedDocument && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Opis kosztu
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
              {data.description || '-'}
            </Typography>
          </CardContent>
        </Card>
      )}

      {!isSimplifiedDocument && data.isPaid && (
        <Card variant="outlined" sx={{ mb: 3, bgcolor: 'success.light' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Informacje o płatności
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Data płatności
                </Typography>
                <Typography variant="body1">{formatExpenseInvoiceDate(data.paidDate)}</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Kwota zapłacona
                </Typography>
                <Typography variant="body1">
                  {data.paidAmount ? formatCurrency(data.paidAmount, data.currency) : '-'}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Notatka
                </Typography>
                <Typography variant="body1">{data.paymentNote || '-'}</Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Dodatkowe informacje
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={3}>
            {data.notes && (
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Notatki
                </Typography>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {data.notes}
                </Typography>
              </Grid>
            )}
            {data.importJobId && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Import z OCR
                </Typography>
                <Button
                  variant="text"
                  size="small"
                  onClick={() =>
                    navigate(
                      ExpenseInvoicesRoutes.importJobDetail.replace(
                        ':importJobId',
                        String(data.importJobId)
                      )
                    )
                  }
                >
                  Zobacz szczegóły importu #{data.importJobId}
                </Button>
              </Grid>
            )}
            <Grid size={{ xs: 12, sm: data.importJobId ? 6 : 12 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Data utworzenia
              </Typography>
              <Typography variant="body2">{formatExpenseInvoiceDate(data.createdAt)}</Typography>
              {data.updatedAt && (
                <>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                    Ostatnia aktualizacja
                  </Typography>
                  <Typography variant="body2">{formatExpenseInvoiceDate(data.updatedAt)}</Typography>
                </>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};
